// Interleave a category's collections into its sorted item list (Groups v2).
// Groups used to be pinned to the top of every list; now they sort alongside
// items by the active sort. A group ranks as if it were its "leading" member:
// for a member-derived sort (year / last engaged / rating / battle rank) it
// takes the strongest value among its members, so a collection floats up next
// to its best content. Title sorts use the group name; Date added uses the
// group's own created_at.
//
// The view's own per-media sort comparator is reused unchanged — each group
// rides through it as a proxy object carrying every candidate field, then maps
// back to its { group, members } pair for rendering.

// A synthetic item-like object so a group can pass through a per-view sort
// comparator. Each comparator only reads the one field its sort cares about, so
// we populate every candidate field with the leading (max) member value. Field
// names span all four media types (release_year / published_year /
// premiered_year etc.); unused ones are simply ignored by a given comparator.
function groupSortProxy(group, members) {
	const maxNum = (key) => {
		let best = null;
		for (const m of members) {
			const v = m[key];
			if (v != null && (best == null || v > best)) best = v;
		}
		return best;
	};
	const maxStr = (key) => {
		let best = "";
		for (const m of members) {
			const v = m[key] ?? "";
			if (v > best) best = v;
		}
		return best || null;
	};
	return {
		__group: group,
		__members: members,
		id: group.id,
		// Title sort → the group name.
		title: group.name ?? "",
		// Date added → the group's own creation date.
		created_at: group.created_at,
		// Member-derived sorts → the leading (best/newest/highest) member value.
		release_year: maxNum("release_year"),
		published_year: maxNum("published_year"),
		premiered_year: maxNum("premiered_year"),
		last_engaged_date: maxStr("last_engaged_date"),
		rating: maxNum("rating"),
	};
}

// Merge a category's groups into its flat item list and sort them together with
// the view's existing comparator. Returns an ordered array of entries, each
// either { group, members } (a collection) or { item } (a single title).
//
// `rankIndex` is the battle-ranking position map (itemId → index, best = 0);
// each group is given its best member's rank so a battle-rank sort places the
// collection next to its top-ranked member. Groups with no ranked members fall
// to the bottom alongside unranked items, exactly as the comparators already do.
export function sortedEntries({ items, groupCards, sort, rankIndex, sortFn }) {
	const augmentedRank = new Map(rankIndex);
	for (const { group, members } of groupCards) {
		let best = Infinity;
		for (const m of members) {
			if (rankIndex.has(m.id)) best = Math.min(best, rankIndex.get(m.id));
		}
		if (best !== Infinity) augmentedRank.set(group.id, best);
	}

	const proxies = groupCards.map(({ group, members }) =>
		groupSortProxy(group, members),
	);
	const merged = sortFn([...items, ...proxies], sort, augmentedRank);
	return merged.map((entry) =>
		entry.__group
			? { group: entry.__group, members: entry.__members }
			: { item: entry },
	);
}
